// Thin wrapper around the dashboard ↔ API worker WebSocket.
// Handles connection, reconnection, JSON envelope, and a simple
// type-router so callers can subscribe to message types.

import { WS_BASE, WORKER_ID, withAuthToken } from "./config";

export interface ControlMessage {
  type: string;
  source?: string;
  payload?: unknown;
  ts?: number;
  [key: string]: unknown;
}

export interface PresenceState {
  workerConnected: boolean;
  electronConnected: boolean;
  adminCount: number;
  lastWorkerMessageAt: number | null;
  lastStatus: unknown;
}

type Handler = (msg: ControlMessage) => void;

const RECONNECT_INITIAL_MS = 1000;
const RECONNECT_MAX_MS = 30000;

export class ControlClient {
  private ws: WebSocket | null = null;
  private handlers = new Map<string, Set<Handler>>();
  private anyHandlers = new Set<Handler>();
  private connHandlers = new Set<(connected: boolean) => void>();
  private presenceHandlers = new Set<(p: PresenceState) => void>();
  private reconnectMs = RECONNECT_INITIAL_MS;
  private reconnectTimer: number | null = null;
  private connected = false;
  private url: string;

  constructor(workerId: string = WORKER_ID) {
    this.url = withAuthToken(`${WS_BASE}/ws/dashboard/${encodeURIComponent(workerId)}`);
  }

  connect(): void {
    if (this.ws) return;
    let socket: WebSocket;
    try {
      socket = new WebSocket(this.url);
    } catch (err) {
      console.error("[control] WebSocket constructor failed", err);
      this.scheduleReconnect();
      return;
    }
    this.ws = socket;

    socket.addEventListener("open", () => {
      this.reconnectMs = RECONNECT_INITIAL_MS;
      this.setConnected(true);
    });

    socket.addEventListener("message", (ev) => {
      let msg: ControlMessage;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      this.dispatch(msg);
    });

    socket.addEventListener("close", () => {
      this.setConnected(false);
      this.ws = null;
      this.scheduleReconnect();
    });

    socket.addEventListener("error", () => {
      // 'error' fires before 'close' — close handler does the reconnect.
    });
  }

  send(msg: ControlMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn("[control] dropping message — socket not open", msg.type);
      return;
    }
    this.ws.send(JSON.stringify(msg));
  }

  on(type: string, fn: Handler): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(fn);
    return () => set!.delete(fn);
  }

  onAny(fn: Handler): () => void {
    this.anyHandlers.add(fn);
    return () => this.anyHandlers.delete(fn);
  }

  onConnectionChange(fn: (connected: boolean) => void): () => void {
    this.connHandlers.add(fn);
    fn(this.connected);
    return () => this.connHandlers.delete(fn);
  }

  onPresence(fn: (p: PresenceState) => void): () => void {
    this.presenceHandlers.add(fn);
    return () => this.presenceHandlers.delete(fn);
  }

  isConnected(): boolean {
    return this.connected;
  }

  private dispatch(msg: ControlMessage): void {
    if (msg.type === "hello" && msg.presence) {
      this.emitPresence(msg.presence as PresenceState);
    }
    if (msg.type === "presence" && msg.payload) {
      this.emitPresence(msg.payload as PresenceState);
    }
    const set = this.handlers.get(msg.type);
    if (set) {
      for (const fn of set) {
        try { fn(msg); } catch (e) { console.error("[control] handler error", e); }
      }
    }
    for (const fn of this.anyHandlers) {
      try { fn(msg); } catch (e) { console.error("[control] handler error", e); }
    }
  }

  private emitPresence(p: PresenceState): void {
    for (const fn of this.presenceHandlers) {
      try { fn(p); } catch (e) { console.error("[control] presence handler error", e); }
    }
  }

  private setConnected(c: boolean): void {
    if (this.connected === c) return;
    this.connected = c;
    for (const fn of this.connHandlers) fn(c);
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    const delay = this.reconnectMs;
    this.reconnectMs = Math.min(this.reconnectMs * 2, RECONNECT_MAX_MS);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}
